import { ref, computed } from 'vue'
import type { KarteCommentTemplate } from '@/types/app'
import {
  useFetchKartePlannerCommentTemplateQuery,
  useAddKartePlannerCommentTemplateMutation,
  useUpdateKartePlannerCommentTemplateMutation,
  useDeleteKartePlannerCommentTemplateMutation,
} from './useKarteCommentTemplate.generated'

// helper
const createEmptyTemplate = (): KarteCommentTemplate => ({
  id: '0', // empty templateはテンプレートにまつわる操作において1つしか存在しないため、'0'という値を決め打ちして大丈夫
  label: '',
  body: '',
  selections: [],
})

export const useKarteCommentTemplate = () => {
  const { result: kartePlannerCommentTemplates, refetch } =
    useFetchKartePlannerCommentTemplateQuery()

  const { mutate: addKartePlannerCommentTemplate } =
    useAddKartePlannerCommentTemplateMutation({})

  const { mutate: updateKartePlannerCommentTemplates } =
    useUpdateKartePlannerCommentTemplateMutation({})

  const { mutate: deleteKartePlannerCommentTemplate } =
    useDeleteKartePlannerCommentTemplateMutation({})

  const templates = computed(() => {
    if (kartePlannerCommentTemplates.value === undefined) {
      return []
    }
    return kartePlannerCommentTemplates.value.viewer
      .kartePlannerCommentTemplates
  })

  // internal api
  const appendNewItem = async (
    label: string,
    body: string,
    selections: string[]
  ): Promise<KarteCommentTemplate> => {
    const newTemplate = { label, body, selections }
    const result = await addKartePlannerCommentTemplate({
      input: newTemplate,
    })

    if (result?.data?.addKartePlannerCommentTemplate === undefined) {
      throw new Error('テンプレートの更新が失敗しました。')
    }

    const currentTemplateIds = templates.value.map(value => value.id)
    const newTemplateIds = result.data.addKartePlannerCommentTemplate.map(
      value => value.id
    )

    const newTemplateId = newTemplateIds?.filter(
      newId => !currentTemplateIds.includes(newId)
    )

    refetch()

    return {
      id: newTemplateId[0],
      ...newTemplate,
    }
  }

  // edit impl
  const isEditing = ref(false)
  const selectedId = ref<Maybe<string>>()

  const selectedItem = computed(() => {
    if (selectedId.value === undefined) {
      return undefined
    }
    return (
      templates.value?.find(item => item.id === selectedId.value) ?? undefined
    )
  })

  const editingItemState = ref(createEmptyTemplate())
  const editingId = ref<Maybe<string>>()
  const edit = () => {
    editingId.value = selectedId.value
    if (selectedItem.value === undefined) {
      return
    }
    editingItemState.value = {
      ...selectedItem.value,
    }
    isEditing.value = true
  }

  const cancelEditing = () => {
    editingItemState.value = createEmptyTemplate()
    editingId.value = undefined
    isEditing.value = false
  }

  const applyEdited = async () => {
    if (editingId.value === undefined) {
      // bug
      return
    }
    const editingItem = editingItemState.value
    await updateKartePlannerCommentTemplates({
      input: {
        id: editingItem.id,
        label: editingItem.label,
        body: editingItem.body,
        selections: editingItem.selections,
      },
    })
    cancelEditing()
  }

  const removeEditingItem = async () => {
    if (editingId.value === undefined) {
      // bug
      return
    }
    await deleteKartePlannerCommentTemplate({ id: editingId.value })
    cancelEditing()

    refetch()
  }

  return {
    templates,
    isEditing: computed(() => isEditing.value),
    isSelected: computed(() => selectedItem.value !== undefined),
    appendNewItem,
    removeEditingItem,
    selectedId,
    selectedItem,
    edit,
    applyEdited,
    cancelEditing,
    editingItemState: computed(() => editingItemState.value),
  }
}
